import { useQuery } from "@tanstack/react-query";
import { useEndpoints } from "./useEndpoints";
import { ChangeEvent, useCallback, useMemo, useState } from "react";
import { renderNameUser } from "../utils/table";
import SlotCounter from "react-slot-counter";
import { HooperMetric } from "../components/DetailTeamPage/SubjectReadinessTab/HooperHistoryModal";

interface SelectedCell {
  athleteId: string;
  athleteName: string;
  metric: HooperMetric;
}

const useSubjectReadinessPage = () => {
  const endpoints = useEndpoints();
  const [searchValue, setSearchValue] = useState<string>("");
  const [date, setDate] = useState<Date | null>(new Date());
  const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);

  const handleCellClick = useCallback((metric: HooperMetric, record: any) => {
    setSelectedCell({
      athleteId: String(record._id),
      athleteName: `${record.firstName} ${record.lastName}`,
      metric,
    });
  }, []);
  const query = useQuery({
    queryKey: ["subject-readiness", date?.toISOString() ?? null],
    queryFn: async () => {
      const response = await endpoints.getSubjectReadiness({
        startDate: date,
        endDate: date,
      });
      return response.data;
    },
  });

  const filteredData = useMemo(() => {
    if (!searchValue) return query.data;
    if (!query.data) return [];
    return query.data.filter((item) =>
      `${item.firstName} ${item.lastName}`
        .toLowerCase()
        .includes(searchValue.toLowerCase()),
    );
  }, [searchValue, query.data]);

  const metricCell = useCallback(
    (metric: HooperMetric) =>
      (_: any, record: any) => {
        const q = Array.isArray(record.hooperQuestionnaire)
          ? record.hooperQuestionnaire[0]
          : record.hooperQuestionnaire;
        if (!q?.[metric]) return "-";
        return (
          <span style={{ cursor: "pointer" }} onClick={() => handleCellClick(metric, record)}>
            <SlotCounter value={q[metric]} />
          </span>
        );
      },
    [handleCellClick],
  );

  const propsTable = useMemo(() => {
    return {
      headers: [
        "Nome",
        "Fatica",
        "Qualità Sonno",
        "Dolore muscoli",
        "Stress",
        "Umore",
        "Media",
        "Ore Sonno",
        "Pront. Allena.",
        "Note",
      ],
      fields: {
        Nome: {
          key: "firstName",
          customComponent: renderNameUser,
        },
        Fatica: {
          key: "fatigue",
          customComponent: metricCell("fatigue"),
        },
        "Qualità Sonno": {
          key: "sleepQuality",
          customComponent: metricCell("sleepQuality"),
        },
        "Dolore muscoli": {
          key: "muscleSoreness",
          customComponent: metricCell("muscleSoreness"),
        },
        Stress: {
          key: "stressLevel",
          customComponent: metricCell("stressLevel"),
        },
        Umore: {
          key: "mood",
          customComponent: metricCell("mood"),
        },
        Media: {
          key: "average",
          customComponent: (_: any, record: any) => {
            const q = Array.isArray(record.hooperQuestionnaire) ? record.hooperQuestionnaire[0] : record.hooperQuestionnaire;
            if (!q) return "-";
            if (q.average != null) return (
              <span style={{ cursor: "pointer" }} onClick={() => handleCellClick("average", record)}>
                <SlotCounter value={q.average.toFixed(1)} />
              </span>
            );
            const keys = [
              "fatigue",
              "sleepQuality",
              "muscleSoreness",
              "stressLevel",
              "mood",
            ];
            const values = keys
              .map((k) => q[k])
              .filter((v) => v != null && v !== undefined);
            if (values.length === 0) return "-";
            const avg =
              values.reduce((sum: number, v: number) => sum + v, 0) /
              values.length;
            return (
              <span style={{ cursor: "pointer" }} onClick={() => handleCellClick("average", record)}>
                <SlotCounter value={avg.toFixed(1)} />
              </span>
            );
          },
        },
        "Ore Sonno": {
          key: "sleepHours",
          customComponent: (_: any, record: any) => {
            const q = Array.isArray(record.hooperQuestionnaire) ? record.hooperQuestionnaire[0] : record.hooperQuestionnaire;
            if (q?.sleepHours == null) return "-";
            return <SlotCounter value={q.sleepHours} />;
          },
        },
        "Pront. Allena.": {
          key: "readinessToTrain",
          customComponent: (_: any, record: any) => {
            const q = Array.isArray(record.hooperQuestionnaire) ? record.hooperQuestionnaire[0] : record.hooperQuestionnaire;
            if (q?.readinessToTrain == null) return "-";
            return <SlotCounter value={q.readinessToTrain} />;
          },
        },
        Note: {
          key: "notes",
          customComponent: (_: any, record: any) => {
            const q = Array.isArray(record.hooperQuestionnaire) ? record.hooperQuestionnaire[0] : record.hooperQuestionnaire;
            if (!q?.notes) return "-";
            return <span title={q.notes}>📝</span>;
          },
        },
      },
    };

  }, [metricCell, handleCellClick]);

  const updateSearchValue = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setSearchValue(e.target.value);
  };

  return {
    query,
    searchValue,
    propsTable,
    filteredData,
    setSearchValue,
    updateSearchValue,
    date,
    setDate,
    selectedCell,
    setSelectedCell,
  };
};

export default useSubjectReadinessPage;
